import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { Ban, SearchX } from 'lucide-react';
import { Alert, Button, EmptyState, PageHeader, ResultState, StepProgress, useHideTabBar } from '@/components/common';
import {
  ConfirmStep,
  PhotosStep,
  PickupStep,
  ProductCard,
  ReasonStep,
  RefundStep,
  ReplacementStep,
  ResolutionStep,
  ReturnStepKey,
  ReturnStepProps,
  StoreCreditStep,
} from '@/components/returns/ReturnSteps';
import { useCustomerOrders } from '@/hooks/useCustomerProducts';
import { useFileClaim } from '@/hooks/useFileClaim';
import { useReturnOptions } from '@/hooks/useReturns';
import { whatsappUrl } from '@/lib/support';
import type { ProductCase } from '@/types/product';
import type { ReturnDraft, SubmittedReturn } from '@/types/returns';
import { formatDay, formatRupees } from '@/utils/productCases';
import { REQUIRED_PHOTOS, RESOLUTION_STEP, returnStatusPath } from '@/utils/returnCases';

const STEPS: ReturnStepKey[] = ['reason', 'photos', 'resolution', 'details', 'confirm', 'pickup'];
const COLUMN = 'tw-mx-auto tw-flex tw-w-full tw-max-w-[680px] tw-flex-col tw-gap-5 md:tw-gap-6';

/** The first step still missing an answer; the form can't be entered past it (e.g. after a refresh). */
function firstOpenStep(draft: ReturnDraft): ReturnStepKey {
  if (!draft.reason) return 'reason';
  const photosDone = REQUIRED_PHOTOS.every((label) => draft.photos.some((p) => p.label === label)) && !draft.photos.some((p) => p.issue);
  if (!photosDone) return 'photos';
  if (!draft.resolution) return 'resolution';
  if (draft.resolution === 'replacement' && !draft.variantId) return 'details';
  if (draft.reason !== 'damaged' && !draft.unusedConfirmed) return 'confirm';
  return 'pickup';
}

function headingFor(step: ReturnStepKey, order: ProductCase, draft: ReturnDraft, replacementInStock: boolean): { title: string; subtitle?: string } {
  const merchant = order.merchant ?? 'The store';
  switch (step) {
    case 'reason':
      return { title: 'Return item', subtitle: order.returnWindow?.endsAt && `Return window open till ${formatDay(order.returnWindow.endsAt)}` };
    case 'photos':
      return { title: 'Add photos of the item', subtitle: `${merchant} uses these to approve your return.` };
    case 'resolution':
      return { title: 'What would you like instead?', subtitle: 'Choose how you want this return resolved.' };
    case 'details':
      if (draft.resolution === 'store_credit') return { title: `${merchant} store credit`, subtitle: `Credit goes to your ${merchant} account after quality check.` };
      if (draft.resolution === 'refund') return { title: 'Refund to original payment', subtitle: `${merchant} refunds the same payment method you used.` };
      return { title: 'Choose your replacement', subtitle: replacementInStock ? 'Same product, new piece. You can switch colour if it’s in stock.' : undefined };
    case 'confirm':
      return { title: 'Check your return details', subtitle: 'Make sure everything’s right before we book the pickup.' };
    case 'pickup':
      return { title: 'Schedule pickup', subtitle: 'A courier collects the item from your address.' };
  }
}

/** R9 — the return window has closed. */
const ReturnClosed: React.FC<{ order: ProductCase }> = ({ order }) => {
  const navigate = useNavigate();
  const { fileClaim } = useFileClaim();
  const merchant = order.merchant ?? 'the store';
  const endsAt = order.returnWindow?.endsAt;
  const days = endsAt && order.deliveredAt ? differenceInCalendarDays(parseISO(endsAt), parseISO(order.deliveredAt)) : undefined;
  return (
    <div className={COLUMN}>
      <PageHeader
        title="Return item"
        subtitle={[order.title, order.deliveredAt && `Delivered ${formatDay(order.deliveredAt)}`].filter(Boolean).join(' · ')}
        back={{ to: '/customer/orders', label: 'Orders' }}
      />
      <ProductCard order={order} line={[order.merchant, order.orderLabel, formatRupees(order.price)].filter(Boolean).join(' · ')} />
      <section className="tw-rounded-2xl tw-border tw-border-ds-border-default tw-bg-ds-surface tw-px-5 tw-py-6">
        <ResultState
          as="h2"
          tone="warning"
          icon={Ban}
          title="Returns are closed for this item"
          description={
            endsAt
              ? `${order.title} could be returned until ${formatDay(endsAt)}${days ? ` — ${days} days after delivery` : ''}.`
              : `The return window for ${order.title} has ended.`
          }
        />
      </section>
      <Alert title="Something wrong with it?">
        If it has a protection plan or is under {merchant}’s warranty, raise a claim and we’ll get it repaired or replaced.
      </Alert>
      <div className="tw-flex tw-flex-col tw-gap-2.5 sm:tw-flex-row">
        {order.plan ? (
          <Button className="sm:tw-flex-1" onClick={() => fileClaim(order.plan!.policy)}>
            Raise a claim
          </Button>
        ) : (
          <Button className="sm:tw-flex-1" href={whatsappUrl(`Hi, I want to raise a claim for ${order.title} (${order.orderLabel ?? ''}).`)} target="_blank">
            Raise a claim
          </Button>
        )}
        <Button variant="secondary" className="sm:tw-flex-1" onClick={() => navigate('/customer/orders')}>
          Back to orders
        </Button>
      </div>
    </div>
  );
};

/** R1–R6 Return item: reason, photos, resolution, its details, confirmation and pickup; R9 when closed. */
const ReturnFlowPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  useHideTabBar();

  const { orders, loading: ordersLoading, error: ordersError, reload } = useCustomerOrders({ withShipmentSamples: true });
  const { options, loading: optionsLoading, error: optionsError } = useReturnOptions(params.get('sample'));
  const [draft, setDraft] = useState<ReturnDraft>({ photos: [], unusedConfirmed: false });
  const update = (patch: Partial<ReturnDraft>) => setDraft((d) => ({ ...d, ...patch }));

  // Free the photo previews when leaving the form.
  const photos = useRef(draft.photos);
  photos.current = draft.photos;
  useEffect(() => () => photos.current.forEach((p) => URL.revokeObjectURL(p.url)), []);

  const order = orders.find((o) => o.orderId === id);
  const orderPath = `/customer/orders/${id}`;
  const loading = ordersLoading || optionsLoading;
  const error = ordersError || optionsError;

  if (loading || error || !order) {
    return (
      <div className={COLUMN}>
        <PageHeader title="Return item" back={{ to: orderPath, label: 'Order details' }} />
        {loading ? (
          <div aria-busy className="tw-h-64 tw-animate-pulse tw-rounded-2xl tw-bg-ds-surface" />
        ) : error ? (
          <div className="tw-flex tw-flex-col tw-gap-3">
            <Alert tone="danger" title="Something went wrong">
              {error}
            </Alert>
            <Button variant="secondary" className="tw-self-start" onClick={reload}>
              Try again
            </Button>
          </div>
        ) : (
          <EmptyState
            icon={SearchX}
            title="Order not found"
            description="This order isn’t linked to your mobile number."
            action={<Button onClick={() => navigate('/customer/orders')}>Back to orders</Button>}
          />
        )}
      </div>
    );
  }

  if (order.returnWindow?.open === false) return <ReturnClosed order={order} />;

  if (!options) {
    return (
      <div className={COLUMN}>
        <PageHeader title="Return item" back={{ to: orderPath, label: 'Order details' }} />
        <Alert title="Returns from your dashboard are coming soon">You can return this item in our return chat today.</Alert>
        <Button onClick={() => navigate(`/customer/return-chat/${id}`)}>Continue in chat</Button>
      </div>
    );
  }

  const requested = params.get('step') as ReturnStepKey;
  const open = firstOpenStep(draft);
  const step = STEPS.includes(requested) && STEPS.indexOf(requested) <= STEPS.indexOf(open) ? requested : STEPS.includes(requested) ? open : 'reason';
  const index = STEPS.indexOf(step);
  const detailsLabel = draft.resolution ? RESOLUTION_STEP[draft.resolution] : 'Details';
  const labels: Record<ReturnStepKey, string> = {
    reason: 'Reason',
    photos: 'Photos',
    resolution: 'Resolution',
    details: detailsLabel,
    confirm: 'Confirm',
    pickup: 'Pickup',
  };

  const stepSearch = (s: ReturnStepKey) => {
    const next = new URLSearchParams(params);
    next.set('step', s);
    return `?${next.toString()}`;
  };
  const go = (s: ReturnStepKey) => {
    setParams(new URLSearchParams(stepSearch(s)));
    window.scrollTo({ top: 0 });
  };
  const previous = STEPS[index - 1];
  const back = previous
    ? { to: stepSearch(previous), label: previous === 'confirm' ? 'Confirm details' : labels[previous] }
    : { to: orderPath, label: 'Order details' };

  const schedule = () => {
    // Returns aren't submitted to the backend yet (see useReturnOptions); only the demo session gets here.
    const slot = options.pickupSlots.find((s) => s.id === draft.slotId);
    const variant = options.variants.find((v) => v.id === draft.variantId);
    const submitted: SubmittedReturn = {
      resolution: draft.resolution!,
      pickup: slot && `${slot.day} · ${slot.window}`,
      replacement: variant && `${order.title} · ${variant.name}`,
    };
    toast.success('Pickup scheduled');
    navigate(`${returnStatusPath(order.orderId!)}${params.get('sample') ? `?sample=${params.get('sample')}` : ''}`, { state: submitted, replace: true });
  };

  const stepProps: ReturnStepProps = {
    order,
    options,
    draft,
    update,
    go,
    orderLine: [order.merchant, order.orderLabel, formatRupees(order.price), order.deliveredAt && `Delivered ${formatDay(order.deliveredAt).slice(0, 6)}`]
      .filter(Boolean)
      .join(' · '),
  };
  const { title, subtitle } = headingFor(step, order, draft, options.variants.some((v) => v.inStock));

  return (
    <div className={COLUMN}>
      <PageHeader title={title} subtitle={subtitle} back={back} />
      <StepProgress current={index + 1} total={STEPS.length} label={labels[step]} />
      {step === 'reason' && <ReasonStep {...stepProps} />}
      {step === 'photos' && <PhotosStep {...stepProps} />}
      {step === 'resolution' && <ResolutionStep {...stepProps} />}
      {step === 'details' &&
        (draft.resolution === 'store_credit' ? (
          <StoreCreditStep {...stepProps} />
        ) : draft.resolution === 'refund' ? (
          <RefundStep {...stepProps} />
        ) : (
          <ReplacementStep {...stepProps} />
        ))}
      {step === 'confirm' && <ConfirmStep {...stepProps} />}
      {step === 'pickup' && <PickupStep {...stepProps} onSchedule={schedule} />}
    </div>
  );
};

export default ReturnFlowPage;
